import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import type { en } from '@/i18n/locales/en'

/* The interface glossary (QW-2, owner decision 2026-10-08).
 *
 * The walkthrough found one thing under several names and one name over several
 * things — «направление» was a leg, a Settings catalogue entry and a payment's
 * in/out (D-1); the chat called the case «кейс» and the quote «работа» (Q-3);
 * the case header's ETD / ETA / Ставка / Прогноз were never explained (C-5).
 * The catalogues now speak one glossary (`glossary.test.ts` guards it), and
 * this page publishes it to the people reading the screens.
 *
 * Static on purpose: no server read, no capability. Every word here is on
 * every role's screens, and the text lives in the `glossary` catalogue group so
 * that parity.ts keeps the two languages in step. This file owns only the
 * ORDER and the GROUPS — GlossaryPage.test.tsx fails if a catalogue term is
 * missing from them, or listed twice.
 *
 * One list, not a table: a phone shows each entry as a stacked card (term and
 * its other-language word on one line, the meaning under it), and from `sm` the
 * same `<dl>` lays out as three columns. The column captions above `sm` are
 * decoration; the other-language word carries its own screen-reader label. */

type TermId = keyof typeof en.glossary.terms
type GroupId = keyof typeof en.glossary.groups

const GROUPS: ReadonlyArray<{ id: GroupId; terms: readonly TermId[] }> = [
  {
    id: 'records',
    terms: [
      'quote',
      'offer',
      'offerTemplate',
      'requestPricing',
      'pricingRequested',
      'pricingReady',
      'case',
      'myTasks',
      'awaitingClient',
    ],
  },
  /* S-13: «Перевозка» heads the group it names — the movement and the
     client's word, beside «Работа», the record — and «Вариант доставки» sits
     under «Маршрут», the word it used to be confused with. */
  {
    id: 'movement',
    terms: [
      'shipment',
      'leg',
      'route',
      'deliveryOption',
      'routeTemplate',
      'truck',
      'plate',
      /* S-18b: the leg's units beside the truck. */
      'unit',
      'container',
      'wagon',
      'awb',
      'bl',
      'carrier',
    ],
  },
  {
    id: 'money',
    terms: [
      'rate',
      'carrierRate',
      'clientPrice',
      'markup',
      'margin',
      'expense',
      'profit',
      'turnover',
      'payment',
    ],
  },
  { id: 'people', terms: ['team', 'operationsOwner', 'participants', 'counterAgent'] },
  { id: 'dates', terms: ['etd', 'eta'] },
]

const ROW = 'grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 sm:grid-cols-[200px_160px_1fr]'

export function GlossaryPage() {
  const { t } = useTranslation()

  return (
    <>
      <PageHeader title={t('glossary.title')} subtitle={t('glossary.subtitle')} />

      <div className="flex flex-col gap-3.5">
        {GROUPS.map((group) => (
          <Card key={group.id}>
            <CardHeader>
              <CardTitle>{t(`glossary.groups.${group.id}`)}</CardTitle>
            </CardHeader>

            <div
              aria-hidden
              className={`${ROW} border-line asa-eyebrow mt-2 hidden border-b px-[18px] pb-2 sm:grid`}
            >
              <span>{t('glossary.columns.term')}</span>
              <span>{t('glossary.columns.other')}</span>
              <span>{t('glossary.columns.meaning')}</span>
            </div>

            <dl className="mt-2 sm:mt-0">
              {group.terms.map((id) => (
                <div
                  key={id}
                  className={`${ROW} border-line-hair border-b px-[18px] py-2.5 last:border-b-0`}
                >
                  <dt className="text-text-heading text-[13px]">
                    {t(`glossary.terms.${id}.term`)}
                  </dt>
                  <dd className="text-text-muted text-[12.5px] leading-[1.6]">
                    <span className="sr-only">{t('glossary.columns.other')}: </span>
                    <span>{t(`glossary.terms.${id}.other`)}</span>
                  </dd>
                  <dd className="text-text-body col-span-2 text-[13px] leading-snug font-light sm:col-span-1">
                    {t(`glossary.terms.${id}.meaning`)}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
        ))}
      </div>
    </>
  )
}
